import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { ConnectionService } from '../connection/connection.service';
import { ObjectStorageService } from '../object-storage/object-storage.service';
import type { ConnectionTestResult } from '../../interfaces/connection.interface';
import type {
  ObjectStorageTestResult,
  TestObjectStorageRequest,
} from '../../interfaces/object-storage.interface';

/**
 * One place to test a connection, regardless of how the backend exposes it.
 *
 * Two distinct test paths exist:
 *  - Standard connections (Database, DataFusion, LLM, Box, Service) are tested
 *    by id via `POST /connection/{id}/test`, so the connection must already be
 *    saved.
 *  - Object-storage connections are tested by posting the (unsaved) config to
 *    `POST /object-storage-test`, so they can be tested before saving.
 *
 * Both paths normalize to a single {@link ConnectionTestResult}, and neither
 * errors: HTTP/transport failures are mapped to `{ success: false, message }`
 * so callers only handle one shape. This replaces three divergent test
 * call-sites (connections list logged to the console, the add-connection dialog
 * showed an inline result, the testing dialog ran its own request).
 */
@Injectable({ providedIn: 'root' })
export class ConnectionTestService {
  readonly #connectionService = inject(ConnectionService);
  readonly #objectStorageService = inject(ObjectStorageService);

  /** Test a saved standard connection by id (`POST /connection/{id}/test`). */
  testStandard(projectId: number, connectionId: number): Observable<ConnectionTestResult> {
    return this.#connectionService.testConnection(projectId, connectionId).pipe(
      map((result) => this.#normalizeStandard(result)),
      catchError((error) => of(this.#failure(error))),
    );
  }

  /**
   * Test an object-storage configuration without saving it
   * (`POST /object-storage-test`).
   */
  testObjectStorage(
    projectId: number,
    request: TestObjectStorageRequest,
  ): Observable<ConnectionTestResult> {
    return this.#objectStorageService.test(projectId, request).pipe(
      map((result) => this.#normalizeObjectStorage(result)),
      catchError((error) => of(this.#failure(error))),
    );
  }

  #normalizeStandard(result: ConnectionTestResult): ConnectionTestResult {
    return {
      success: result.success,
      message: result.message ?? this.#defaultMessage(result.success),
      filesCount: result.filesCount,
    };
  }

  #normalizeObjectStorage(result: ObjectStorageTestResult): ConnectionTestResult {
    return {
      success: result.success,
      message: result.error ?? this.#defaultMessage(result.success),
    };
  }

  #failure(error: unknown): ConnectionTestResult {
    const err = error as { error?: { message?: string }; message?: string };
    const message = err?.error?.message ?? err?.message ?? this.#defaultMessage(false);
    return { success: false, message };
  }

  #defaultMessage(success: boolean): string {
    return success ? 'Connection successful' : 'Connection test failed';
  }
}
